<template>
  <div class="custom-date-picker">
    <v-menu
      ref="menu"
      v-model="menuOpen"
      :close-on-content-click="false"
      :close-on-click="false"
      :disabled="disabled"
      transition="scale-transition"
      offset-y
      min-width="320px"
      max-width="340px"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="dateTimeFormatted"
          :label="label"
          :prepend-inner-icon="prependInnerIcon"
          readonly
          outlined
          :dense="dense"
          :disabled="disabled"
          clearable
          :clear-icon="disabled ? '' : 'mdi-close'"
          @click:clear="clearDateTime"
          :rules="rules"
          :error-messages="errorMessages"
          v-bind="attrs"
          v-on="disabled ? {} : on"
          class="date-picker-field"
        ></v-text-field>
      </template>
      <v-card class="date-time-picker-card">
        <v-date-picker
          v-if="!showTimePicker"
          v-model="dateSelected"
          full-width
          :locale="'es'"
          :first-day-of-week="1"
          :min="minDateFormatted"
          :max="maxDateFormatted"
          scrollable
          :events="eventDates"
          :event-color="getEventColor"
          :allowed-dates="allowedDates"
          @input="onDatePicked"
        ></v-date-picker>
        <div v-if="showTimePicker" class="ios-time-picker">
          <div class="v-picker__title primary">
            <div class="v-date-picker-title__year">{{ dateHeaderLabel }}</div>
            <div class="v-date-picker-title__date">
              <div>{{ timeSelected || '--:--' }}</div>
            </div>
          </div>
          <ios-wheel-picker
            ref="timeWheel"
            :columns="timeColumns"
            :value="timeWheelValue"
            :item-height="36"
            :visible-count="5"
            @change="onTimeWheelChange"
          ></ios-wheel-picker>
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small color="grey" @click="cancelPicker">Cancelar</v-btn>
          <v-btn v-if="showTimePicker" text small color="primary" @click="showTimePicker = false">Calendario</v-btn>
          <v-btn v-if="!showTimePicker && dateSelected" text small color="primary" @click="showTimePicker = true">Hora</v-btn>
          <v-btn v-if="showTimePicker" text small color="primary" @click="confirmDateTime">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import moment from 'moment'
import 'moment-timezone'
import MobileDetect from 'mobile-detect'
import IosWheelPicker from './IosWheelPicker'

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const DAYS_ES = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']

const pad = value => (value < 10 ? '0' + value : String(value))

export default {
  name: 'CustomDateTimePicker',
  components: { IosWheelPicker },
  props: {
    value: {
      type: [String, Number, Date, null],
      default: null
    },
    min: {
      type: [String, Number, Date],
      default: undefined
    },
    max: {
      type: [String, Number, Date],
      default: undefined
    },
    label: {
      type: String,
      default: 'Fecha y hora'
    },
    lastMovement: {
      type: Object,
      default: null
    },
    movements: {
      type: Array,
      default: () => []
    },
    isAssignment: {
      type: Boolean,
      default: false
    },
    isMovement: {
      type: Boolean,
      default: false
    },
    prependInnerIcon: {
      type: String,
      default: 'mdi-calendar-clock'
    },
    rules: {
      type: Array,
      default: () => []
    },
    errorMessages: {
      type: [Array, String],
      default: ''
    },
    dense: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Salto entre minutos de la rueda (1 = todos los minutos, 5 = estilo iPhone)
    minuteInterval: {
      type: Number,
      default: 1
    }
  },
  computed: {
    minDateFormatted() {
      if (!this.min) return undefined
      if (typeof this.min === 'number') {
        return moment.unix(this.min).tz('Europe/Madrid').format('YYYY-MM-DD')
      }
      return moment(this.min).tz('Europe/Madrid').format('YYYY-MM-DD')
    },
    maxDateFormatted() {
      if (!this.max) return undefined
      if (typeof this.max === 'number') {
        return moment.unix(this.max).tz('Europe/Madrid').format('YYYY-MM-DD')
      }
      return moment(this.max).tz('Europe/Madrid').format('YYYY-MM-DD')
    },
    minTimeForSelectedDate() {
      if (!this.min || !this.dateSelected) return undefined
      
      const minMoment = this.getMinMoment()
      const selectedDate = moment.tz(this.dateSelected, 'Europe/Madrid')
      
      // Si la fecha seleccionada es igual a la fecha mínima, devolver la hora mínima
      if (selectedDate.isSame(minMoment, 'day')) {
        return minMoment.format('HH:mm')
      }
      return undefined
    },
    maxTimeForSelectedDate() {
      if (!this.max || !this.dateSelected) return undefined
      
      const maxMoment = this.getMaxMoment()
      const selectedDate = moment.tz(this.dateSelected, 'Europe/Madrid')
      
      // Si la fecha seleccionada es igual a la fecha máxima, devolver la hora máxima
      if (selectedDate.isSame(maxMoment, 'day')) {
        return maxMoment.format('HH:mm')
      }
      return undefined
    },
    allowedHours() {
      if (!this.dateSelected) return undefined
      
      let restrictions = []
      
      // Restricción por fecha mínima
      if (this.min) {
        const minMoment = this.getMinMoment()
        const selectedDate = moment.tz(this.dateSelected, 'Europe/Madrid')
        
        if (selectedDate.isSame(minMoment, 'day')) {
          const minHour = minMoment.hour()
          restrictions.push(hour => hour >= minHour)
        }
      }
      
      // Restricción por eventos - BLOQUEAR el rango ocupado
      const info = this.getEventInfo(this.dateSelected)
      if (info.isEvent) {
        const incompleteMovements = this.movements.filter(movement => movement.status !== 'completed')
        const selectedDate = moment.tz(this.dateSelected, 'Europe/Madrid')
        
        // Recopilar todos los rangos bloqueados en esta fecha
        const blockedRanges = []
        
        incompleteMovements.forEach(movement => {
          if (movement.type === 'assignment' || movement.type === 'move') {
            let start = null
            let end = null
            
            if (movement.datetime) {
              if (typeof movement.datetime === 'number') {
                start = moment.unix(movement.datetime).tz('Europe/Madrid')
              } else if (typeof movement.datetime === 'string') {
                start = moment(movement.datetime).tz('Europe/Madrid')
              } else if (movement.datetime instanceof Date) {
                start = moment(movement.datetime).tz('Europe/Madrid')
              }
            }
            
            if (movement.end_date) {
              if (typeof movement.end_date === 'number') {
                end = moment.unix(movement.end_date).tz('Europe/Madrid')
              } else if (typeof movement.end_date === 'string') {
                end = moment(movement.end_date).tz('Europe/Madrid')
              } else if (movement.end_date instanceof Date) {
                end = moment(movement.end_date).tz('Europe/Madrid')
              }
            }
            
            // Agregar rango bloqueado si el día seleccionado está en el rango del movimiento
            if (start && end && start.isValid() && end.isValid()) {
              // Verificar si la fecha seleccionada está en el rango del movimiento
              if (selectedDate.isSameOrAfter(start, 'day') && selectedDate.isSameOrBefore(end, 'day')) {
                // Caso 1: Mismo día (inicio y fin en el mismo día)
                if (start.isSame(end, 'day')) {
                  blockedRanges.push({
                    startHour: start.hour(),
                    startMinute: start.minute(),
                    endHour: end.hour(),
                    endMinute: end.minute()
                  })
                } else if (selectedDate.isSame(start, 'day')) {
                  // Caso 2: Día de inicio (bloquear desde la hora de inicio hasta el final del día)
                  blockedRanges.push({
                    startHour: start.hour(),
                    startMinute: start.minute(),
                    endHour: 23,
                    endMinute: 59
                  })
                } else if (selectedDate.isSame(end, 'day')) {
                  // Caso 3: Día de fin (bloquear desde el inicio del día hasta la hora de fin)
                  blockedRanges.push({
                    startHour: 0,
                    startMinute: 0,
                    endHour: end.hour(),
                    endMinute: end.minute()
                  })
                } else {
                  // Caso 4: Día intermedio (bloquear todo el día)
                  blockedRanges.push({
                    startHour: 0,
                    startMinute: 0,
                    endHour: 23,
                    endMinute: 59
                  })
                }
              }
            }
          }
        })
        
        if (blockedRanges.length > 0) {
          // Crear restricción que bloquee las horas en los rangos ocupados
          restrictions.push(hour => {
            for (const range of blockedRanges) {
              // Verificar si la hora está completamente dentro del rango
              if (hour > range.startHour && hour < range.endHour) {
                // Hora completamente bloqueada (está entre medio)
                return false
              }
              
              // Si es la hora de inicio, verificar si hay algún minuto bloqueado
              if (hour === range.startHour && range.startMinute < 60) {
                // Hay minutos bloqueados en esta hora (desde startMinute hasta 59)
                return false
              }
              
              // Si es la hora de fin, verificar si hay algún minuto bloqueado
              if (hour === range.endHour && range.endMinute > 0) {
                // Hay minutos bloqueados en esta hora (desde 0 hasta endMinute)
                return false
              }
            }
            return true
          })
        }
      }
      
      if (restrictions.length === 0) return undefined
      return hour => restrictions.every(restrict => restrict(hour))
    },
    allowedMinutes() {
      if (!this.dateSelected || !this.timeSelected) return undefined
      
      const selectedDate = moment.tz(this.dateSelected, 'Europe/Madrid')
      const currentHour = parseInt(this.timeSelected.split(':')[0])
      
      // Restricción por fecha mínima
      if (this.min) {
        const minMoment = this.getMinMoment()
        if (selectedDate.isSame(minMoment, 'day') && currentHour === minMoment.hour()) {
          const minMinute = minMoment.minute()
          return minute => minute > minMinute
        }
      }
      
      // Restricción por eventos - BLOQUEAR minutos en rangos ocupados
      const info = this.getEventInfo(this.dateSelected)
      if (info.isEvent) {
        const incompleteMovements = this.movements.filter(movement => movement.status !== 'completed')
        
        // Recopilar todos los rangos bloqueados para esta hora específica
        const blockedRanges = []
        
        incompleteMovements.forEach(movement => {
          if (movement.type === 'assignment' || movement.type === 'move') {
            let start = null
            let end = null
            
            if (movement.datetime) {
              if (typeof movement.datetime === 'number') {
                start = moment.unix(movement.datetime).tz('Europe/Madrid')
              } else if (typeof movement.datetime === 'string') {
                start = moment(movement.datetime).tz('Europe/Madrid')
              } else if (movement.datetime instanceof Date) {
                start = moment(movement.datetime).tz('Europe/Madrid')
              }
            }
            
            if (movement.end_date) {
              if (typeof movement.end_date === 'number') {
                end = moment.unix(movement.end_date).tz('Europe/Madrid')
              } else if (typeof movement.end_date === 'string') {
                end = moment(movement.end_date).tz('Europe/Madrid')
              } else if (movement.end_date instanceof Date) {
                end = moment(movement.end_date).tz('Europe/Madrid')
              }
            }
            
            if (start && end && start.isValid() && end.isValid()) {
              // Verificar si la fecha seleccionada está dentro del rango del movimiento
              if (selectedDate.isSameOrAfter(start, 'day') && selectedDate.isSameOrBefore(end, 'day')) {
                // Caso 1: Inicio y fin el mismo día
                if (start.isSame(end, 'day')) {
                  blockedRanges.push({
                    startHour: start.hour(),
                    startMinute: start.minute(),
                    endHour: end.hour(),
                    endMinute: end.minute()
                  })
                } else if (selectedDate.isSame(start, 'day')) {
                  // Caso 2: Día de inicio (bloquear desde hora de inicio hasta fin del día)
                  blockedRanges.push({
                    startHour: start.hour(),
                    startMinute: start.minute(),
                    endHour: 23,
                    endMinute: 59
                  })
                } else if (selectedDate.isSame(end, 'day')) {
                  // Caso 3: Día de fin (bloquear desde inicio del día hasta hora de fin)
                  blockedRanges.push({
                    startHour: 0,
                    startMinute: 0,
                    endHour: end.hour(),
                    endMinute: end.minute()
                  })
                } else {
                  // Caso 4: Día intermedio (bloquear todo el día)
                  blockedRanges.push({
                    startHour: 0,
                    startMinute: 0,
                    endHour: 23,
                    endMinute: 59
                  })
                }
              }
            }
          }
        })
        
        if (blockedRanges.length > 0) {
          return minute => {
            for (const range of blockedRanges) {
              // Si estamos en la hora de inicio del rango
              if (currentHour === range.startHour) {
                // Bloquear minutos desde startMinute hasta 59 (si el rango continúa)
                if (currentHour < range.endHour) {
                  // El rango continúa en horas posteriores, bloquear desde startMinute
                  if (minute >= range.startMinute) {
                    return false
                  }
                } else if (currentHour === range.endHour) {
                  // Inicio y fin en la misma hora, bloquear entre startMinute y endMinute
                  if (minute >= range.startMinute && minute <= range.endMinute) {
                    return false
                  }
                }
              } else if (currentHour === range.endHour) {
                // Si estamos en la hora de fin del rango
                // Bloquear minutos desde 0 hasta endMinute
                if (minute <= range.endMinute) {
                  return false
                }
              } else if (currentHour > range.startHour && currentHour < range.endHour) {
                // Si estamos en una hora entre inicio y fin
                return false
              }
            }
            return true
          }
        }
      }
      
      return undefined
    },
    eventDates() {
      const eventDates = []
      
      // Filtrar movimientos que no estén completados
      const incompleteMovements = this.movements.filter(movement => movement.status !== 'completed')

      incompleteMovements.forEach((movement, index) => {
        let start = null
        let end = null
        
       if (movement.type === 'assignment' || movement.type === 'move') {
          // Para movimientos de tipo 'assignment', usar datetime y end_date
          if (movement.datetime) {
            if (typeof movement.datetime === 'number') {
              start = moment.unix(movement.datetime).tz('Europe/Madrid')
            } else if (typeof movement.datetime === 'string') {
              start = moment(movement.datetime).tz('Europe/Madrid')
            } else if (movement.datetime instanceof Date) {
              start = moment(movement.datetime).tz('Europe/Madrid')
            }
          }
          
          if (movement.end_date) {
            if (typeof movement.end_date === 'number') {
              end = moment.unix(movement.end_date).tz('Europe/Madrid')
            } else if (typeof movement.end_date === 'string') {
              end = moment(movement.end_date).tz('Europe/Madrid')
            } else if (movement.end_date instanceof Date) {
              end = moment(movement.end_date).tz('Europe/Madrid')
            }
          }
        }
        
        // Si tenemos fecha de inicio válida
        if (start && start.isValid()) {
          if (end && end.isValid() && end.isAfter(start)) {
            // Si tenemos ambas fechas y end es después de start, crear rango
            let current = start.clone()
            while (current.isSameOrBefore(end, 'day')) {
              const dateStr = current.format('YYYY-MM-DD')
              if (!eventDates.includes(dateStr)) {
                eventDates.push(dateStr)
              }
              current.add(1, 'day')
            }
          } else {
            // Si solo tenemos fecha de inicio o end no es válido/después, solo agregar la fecha de inicio
            const dateStr = start.format('YYYY-MM-DD')
            if (!eventDates.includes(dateStr)) {
              eventDates.push(dateStr)
            }
          }
        }
      })
      
      return eventDates
    },
    allowedDates() {
      return date => {
        // Si hay fecha mínima, deshabilitar fechas anteriores
        if (this.min) {
          const minDate = moment(this.minDateFormatted)
          const currentDate = moment(date)
          if (currentDate.isBefore(minDate, 'day')) {
            return false
          }
        }
        
        // Si hay fecha máxima, deshabilitar fechas posteriores
        if (this.max) {
          const maxDate = moment(this.maxDateFormatted)
          const currentDate = moment(date)
          if (currentDate.isAfter(maxDate, 'day')) {
            return false
          }
        }
        
        // Verificar eventos en la fecha
        const info = this.getEventInfo(date)
        
        if (info.isEvent) {
          // Si es parcial (naranja), permitir selección
          if (info.color === 'orange lighten-2') {
            return true
          }
          // Si es completo (rojo), deshabilitar
          return false
        }
        
        return true
      }
    },
    // --- Rueda de hora estilo iOS ---
    timeWheelValue() {
      const parts = (this.timeSelected || '00:00').split(':')
      return { h: parseInt(parts[0]), mi: parseInt(parts[1]) }
    },
    hourItems() {
      const allowed = this.allowedHours
      // Límites que antes aplicaba el :min/:max del v-time-picker
      const minHour = this.minTimeForSelectedDate ? parseInt(this.minTimeForSelectedDate.split(':')[0]) : null
      const maxHour = this.maxTimeForSelectedDate ? parseInt(this.maxTimeForSelectedDate.split(':')[0]) : null
      const items = []
      for (let hour = 0; hour <= 23; hour++) {
        let disabled = typeof allowed === 'function' ? !allowed(hour) : false
        if (minHour !== null && hour < minHour) disabled = true
        if (maxHour !== null && hour > maxHour) disabled = true
        items.push({ value: hour, label: pad(hour), disabled: disabled })
      }
      return items
    },
    minuteItems() {
      const allowed = this.allowedMinutes
      const step = this.minuteInterval > 0 ? this.minuteInterval : 1
      const current = this.timeWheelValue.mi
      const values = []
      for (let minute = 0; minute <= 59; minute += step) {
        values.push(minute)
      }
      // Mantener visible el minuto actual aunque no encaje con el intervalo
      if (values.indexOf(current) === -1 && !isNaN(current)) {
        values.push(current)
        values.sort((a, b) => a - b)
      }
      // Límites que antes aplicaba el :min/:max del v-time-picker
      const hour = this.timeWheelValue.h
      const min = this.minTimeForSelectedDate ? this.minTimeForSelectedDate.split(':').map(Number) : null
      const max = this.maxTimeForSelectedDate ? this.maxTimeForSelectedDate.split(':').map(Number) : null

      return values.map(minute => {
        let disabled = typeof allowed === 'function' ? !allowed(minute) : false
        if (min && hour === min[0] && minute < min[1]) disabled = true
        if (max && hour === max[0] && minute > max[1]) disabled = true
        return { value: minute, label: pad(minute), disabled: disabled }
      })
    },
    timeColumns() {
      return [
        { key: 'h', flex: 1, items: this.hourItems },
        { key: 'mi', flex: 1, items: this.minuteItems }
      ]
    },
    dateHeaderLabel() {
      if (!this.dateSelected) return ''
      const m = moment(this.dateSelected, 'YYYY-MM-DD')
      return `${DAYS_ES[m.day()]}, ${m.date()} ${MONTHS_ES[m.month()]} ${m.year()}`
    },
    isMobile() {
      return this.md.mobile() !== null
    }
  },
  data () {
    return {
      menuOpen: false,
      dateSelected: null,
      timeSelected: null,
      dateTimeFormatted: '',
      showTimePicker: false,
      md: new MobileDetect(navigator.userAgent)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        this.initializeFromProp(newVal)
      }
    },
    movements: {
      immediate: true,
      handler (newVal) {
        // Watch para detectar cambios en movements
      }
    },
    min (newVal) {
      // Watch para detectar cambios en min
    },
    max (newVal) {
      // Watch para detectar cambios en max
    },
    disabled (newVal) {
      if (newVal) {
        this.menuOpen = false
      }
    },
    showTimePicker (visible) {
      if (!visible) return
      if (!this.timeSelected) {
        this.timeSelected = this.minTimeForSelectedDate || '00:00'
        this.ensureAllowedTime()
      }
      this.syncTimeWheel()
    },
    menuOpen (open) {
      if (open && this.showTimePicker) this.syncTimeWheel()
    }
  },
  methods: {
    getEventColor(date) {
      return this.getEventInfo(date).color
    },
    getEventInfo(date) {
      // Filtrar movimientos que no estén completados
      const incompleteMovements = this.movements.filter(movement => movement.status !== 'completed')
      
      let isEvent = false
      let color = null
      
      incompleteMovements.forEach((movement, index) => {
        let start = null
        let end = null
        
        if (movement.type === 'assignment' || movement.type === 'move') {
          if (movement.datetime) {
            if (typeof movement.datetime === 'number') {
              start = moment.unix(movement.datetime).tz('Europe/Madrid')
            } else if (typeof movement.datetime === 'string') {
              start = moment(movement.datetime).tz('Europe/Madrid')
            } else if (movement.datetime instanceof Date) {
              start = moment(movement.datetime).tz('Europe/Madrid')
            }
          }
          
          if (movement.end_date) {
            if (typeof movement.end_date === 'number') {
              end = moment.unix(movement.end_date).tz('Europe/Madrid')
            } else if (typeof movement.end_date === 'string') {
              end = moment(movement.end_date).tz('Europe/Madrid')
            } else if (movement.end_date instanceof Date) {
              end = moment(movement.end_date).tz('Europe/Madrid')
            }
          }
        }
        
        if (start && start.isValid()) {
          const dateMoment = moment(date).tz('Europe/Madrid')
          if (end && end.isValid() && end.isAfter(start)) {
            // Verificar si la fecha está en el rango
            if (dateMoment.isSameOrAfter(start, 'day') && dateMoment.isSameOrBefore(end, 'day')) {
              isEvent = true
              const isStartDay = dateMoment.isSame(start, 'day')
              const isEndDay = dateMoment.isSame(end, 'day')
              const startOfDay = dateMoment.clone().startOf('day')
              const endOfDay = dateMoment.clone().endOf('day')
              
              // Es parcial si:
              // 1. Es el día de inicio Y el movimiento NO empieza a las 00:00
              // 2. Es el día final Y el movimiento NO termina a las 23:59
              const isPartialStart = isStartDay && !start.isSame(startOfDay, 'minute')
              const isPartialEnd = isEndDay && !end.isSame(endOfDay, 'minute')
              const isPartial = isPartialStart || isPartialEnd
              
              color = isPartial ? 'orange lighten-2' : 'red lighten-2'
            }
          } else {
            // Solo fecha de inicio
            if (dateMoment.isSame(start, 'day')) {
              isEvent = true
              color = 'red lighten-2'
            }
          }
        }
      })
      
      return { isEvent, color }
    },
    getMinMoment() {
      if (!this.min) return null
      if (typeof this.min === 'number') {
        return moment.unix(this.min).tz('Europe/Madrid')
      }
      return moment(this.min).tz('Europe/Madrid')
    },
    getMaxMoment() {
      if (!this.max) return null
      if (typeof this.max === 'number') {
        return moment.unix(this.max).tz('Europe/Madrid')
      }
      return moment(this.max).tz('Europe/Madrid')
    },
    initializeFromProp (value) {
      if (!value) {
        this.dateSelected = null
        this.timeSelected = null
        this.dateTimeFormatted = ''
        return
      }
      try {
        let momentDate
        if (typeof value === 'number') {
          momentDate = moment.unix(value).tz('Europe/Madrid')
        } else if (typeof value === 'string') {
          momentDate = moment(value).tz('Europe/Madrid')
        } else if (value instanceof Date) {
          momentDate = moment(value).tz('Europe/Madrid')
        }
        if (momentDate && momentDate.isValid()) {
          this.dateSelected = momentDate.format('YYYY-MM-DD')
          this.timeSelected = momentDate.format('HH:mm')
          this.dateTimeFormatted = momentDate.format('DD/MM/YYYY HH:mm')
        } else {
          this.dateSelected = null
          this.timeSelected = null
          this.dateTimeFormatted = ''
        }
      } catch (error) {
        this.dateSelected = null
        this.timeSelected = null
        this.dateTimeFormatted = ''
      }
    },
    clearDateTime () {
      if (this.disabled) return
      this.dateSelected = null
      this.timeSelected = null
      this.dateTimeFormatted = ''
      this.$emit('input', null)
    },
    onDatePicked (date) {
      this.dateSelected = date
      // Limpiar la hora al cambiar la fecha
      this.timeSelected = null
      this.showTimePicker = true
    },
    syncTimeWheel (retry) {
      this.$nextTick(() => {
        if (this.$refs.timeWheel) {
          this.$refs.timeWheel.sync()
        } else if (!retry) {
          // El contenido del menú se monta de forma diferida la primera vez
          setTimeout(() => this.syncTimeWheel(true), 60)
        }
      })
    },

    onTimeWheelChange (payload) {
      const current = this.timeWheelValue
      const hour = payload.key === 'h' ? payload.value : current.h
      const minute = payload.key === 'mi' ? payload.value : current.mi
      this.timeSelected = `${pad(hour)}:${pad(minute)}`
      this.ensureAllowedTime()
    },

    // Descarta lo elegido sin confirmar y vuelve al valor confirmado (el de la prop)
    cancelPicker () {
      this.menuOpen = false
      this.showTimePicker = false
      this.initializeFromProp(this.value)
    },

    // Ajusta la hora seleccionada si no está permitida para la fecha actual
    ensureAllowedTime () {
      if (!this.timeSelected) return
      const parts = this.timeSelected.split(':')
      let hour = parseInt(parts[0])
      let minute = parseInt(parts[1])

      const hourItem = this.hourItems.find(item => item.value === hour)
      if (!hourItem || hourItem.disabled) {
        const firstHour = this.hourItems.find(item => !item.disabled)
        if (!firstHour) return
        hour = firstHour.value
        minute = 0
      }
      this.timeSelected = `${pad(hour)}:${pad(minute)}`

      const minuteItem = this.minuteItems.find(item => item.value === minute)
      if (!minuteItem || minuteItem.disabled) {
        const firstMinute = this.minuteItems.find(item => !item.disabled)
        if (firstMinute) this.timeSelected = `${pad(hour)}:${pad(firstMinute.value)}`
      }
    },

    confirmDateTime () {
      this.formatSelectedDateTime()
      this.menuOpen = false
      this.emitValue()
    },
    formatSelectedDateTime () {
      if (!this.dateSelected || !this.timeSelected) {
        this.dateTimeFormatted = ''
        return
      }
      try {
        const dt = moment.tz(`${this.dateSelected} ${this.timeSelected}`, 'YYYY-MM-DD HH:mm', 'Europe/Madrid')
        this.dateTimeFormatted = dt.format('DD/MM/YYYY HH:mm')
      } catch (error) {
        this.dateTimeFormatted = ''
      }
    },
    emitValue () {
      if (this.isAssignment || this.isMovement) {
        if (this.dateSelected && this.timeSelected) {
          const dt = moment.tz(`${this.dateSelected} ${this.timeSelected}`, 'YYYY-MM-DD HH:mm', 'Europe/Madrid')
          this.$emit('input', dt.unix())
        } else {
          this.$emit('input', null)
        }
        return
      }

      if (!this.dateSelected || !this.timeSelected) {
        this.$emit('input', null)
        return
      }

      try {
        const dt = moment.tz(`${this.dateSelected} ${this.timeSelected}`, 'YYYY-MM-DD HH:mm', 'Europe/Madrid')
        this.$emit('input', dt.unix())
      } catch (error) {
        console.error('Error al emitir fecha:', error)
        this.$emit('input', null)
      }
    }
  }
}
</script>

<style scoped>
.custom-date-picker {
  width: 100%;
}
.date-time-picker-card {
  overflow: hidden;
}
.date-time-picker-card .v-card__actions {
  flex-wrap: wrap;
  padding: 4px 8px;
}
.date-time-picker-card .v-card__actions .v-btn {
  min-width: 0;
  padding: 0 10px;
}
.date-picker-field.v-text-field--disabled {
  color: rgba(0, 0, 0, 0.6) !important;
}
.date-picker-field.v-text-field--disabled .v-label {
  color: rgba(0, 0, 0, 0.6) !important;
}
.date-picker-field.v-text-field--disabled .v-icon {
  color: rgba(0, 0, 0, 0.38) !important;
}

.ios-time-picker {
  padding-bottom: 4px;
}

/* Mismo título azul que el v-date-picker */
.ios-time-picker .v-picker__title {
  border-radius: 0;
}

.ios-time-picker .v-date-picker-title__year {
  opacity: 0.6;
}

.ios-time-picker .ios-wheel {
  margin-top: 8px;
}

/* Estilos responsive para móviles */
@media (max-width: 600px) {
  .v-card-actions {
    flex-direction: column !important;
    gap: 8px;
    padding: 16px !important;
  }
  
  .v-card-actions .v-btn {
    width: 100% !important;
    margin: 0 !important;
  }
  
  .v-card-actions .v-spacer {
    display: none !important;
  }
}
</style>
